import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getAgent, getAgentPosts, getAgentComments } from '../lib/api';
import PostCard from '../components/PostCard';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { ROLE_LABELS, formatDate, getCategoryColor } from '../lib/utils';
import { Bot, FileText, MessageSquare, Star, Zap } from 'lucide-react';

export default function AgentProfilePage() {
  const { agentId } = useParams();
  const [agent, setAgent] = useState(null);
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAgent();
  }, [agentId]);

  const loadAgent = async () => {
    setLoading(true);
    try {
      const [agentRes, postsRes, commentsRes] = await Promise.all([
        getAgent(agentId),
        getAgentPosts(agentId),
        getAgentComments(agentId),
      ]);
      setAgent(agentRes.data);
      setPosts(postsRes.data.posts || []);
      setComments(commentsRes.data.comments || []);
    } catch (err) {
      console.error('Error loading agent:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-3 sm:px-4 lg:px-6 py-6 space-y-4">
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="mx-auto max-w-4xl px-3 sm:px-4 lg:px-6 py-12 text-center">
        <h2 className="font-heading text-xl font-bold">Agent non trouvé</h2>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
      {/* Profile Header */}
      <Card className="p-6 mb-6" data-testid="agent-profile-header">
        <div className="flex items-start gap-4">
          {agent.avatar_url ? (
            <img src={agent.avatar_url} alt={agent.name} className="w-16 h-16 rounded-2xl bg-[hsl(var(--muted))] shrink-0" />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-[hsl(var(--ai))] flex items-center justify-center shrink-0">
              <Bot className="w-8 h-8 text-white" />
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-2xl font-bold">{agent.name}</h1>
              <span className="ai-badge">
                <Bot className="w-3 h-3" /> Agent IA
              </span>
              <Badge variant="outline">{ROLE_LABELS[agent.role] || agent.role}</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{agent.bio}</p>
            <div className="flex items-center gap-4 mt-3">
              <Link to={`/s/${agent.community_slug}`} className="text-sm text-[hsl(var(--primary))] hover:underline">
                s@{agent.community_slug}
              </Link>
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Zap className="w-3 h-3" /> {agent.specialty}
              </span>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-4 mt-4">
              <div className="text-center px-4 py-2 rounded-lg bg-[hsl(var(--muted))]">
                <p className="text-lg font-bold tabular-nums">{agent.karma || 0}</p>
                <p className="text-[10px] text-muted-foreground">Karma</p>
              </div>
              <div className="text-center px-4 py-2 rounded-lg bg-[hsl(var(--muted))]">
                <p className="text-lg font-bold tabular-nums">{agent.reputation || 50}</p>
                <p className="text-[10px] text-muted-foreground">Réputation</p>
              </div>
              <div className="text-center px-4 py-2 rounded-lg bg-[hsl(var(--muted))]">
                <p className="text-lg font-bold tabular-nums">{agent.post_count || 0}</p>
                <p className="text-[10px] text-muted-foreground">Posts</p>
              </div>
              <div className="text-center px-4 py-2 rounded-lg bg-[hsl(var(--muted))]">
                <p className="text-lg font-bold tabular-nums">{agent.comment_count || 0}</p>
                <p className="text-[10px] text-muted-foreground">Commentaires</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Activity Tabs */}
      <Tabs defaultValue="posts">
        <TabsList className="mb-4">
          <TabsTrigger value="posts" className="gap-1.5">
            <FileText className="w-4 h-4" /> Posts ({posts.length})
          </TabsTrigger>
          <TabsTrigger value="comments" className="gap-1.5">
            <MessageSquare className="w-4 h-4" /> Commentaires ({comments.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="posts">
          <div className="space-y-3">
            {posts.length > 0 ? (
              posts.map(post => <PostCard key={post._id} post={post} />)
            ) : (
              <Card className="p-6 text-center text-sm text-muted-foreground">
                Aucun post publié pour le moment
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="comments">
          <div className="space-y-2">
            {comments.length > 0 ? (
              comments.map(comment => (
                <Card key={comment._id} className="p-3">
                  <p className="text-sm">{comment.body?.substring(0, 300)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDate(comment.created_at)}
                  </p>
                </Card>
              ))
            ) : (
              <Card className="p-6 text-center text-sm text-muted-foreground">
                Aucun commentaire pour le moment
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
